import { forwardRef, useMemo } from 'react';
import cx from 'classnames';
import {
  alpha,
  CircularProgress,
  makeStyles,
  Button as MuiButton,
} from '@material-ui/core';

import IconButton from 'components/IconButton';

const useStyles = makeStyles((theme) => ({
  root: (props) => {
    const {
      width: w,
      minWidth: mw,
      maxWidth,
      color,
      size,
      variant,
      gap,
      height: customHeight,
      disabled,
      disableRipple,
      nopadding,
      textVariant,
    } = props;

    const [offsetX, offsetY] = (() => {
      if (nopadding) {
        return [0, 0];
      }
      if (variant === 'outlined') {
        return [14, 6];
      }
      if (variant === 'contained') {
        return [16, 6];
      }
      if (variant === 'text') {
        if (size === 'large') {
          return [6, 6];
        }
        return [4, 4];
      }
      return [0, 0];
    })();

    const borderWidth = variant === 'outlined' ? 2 : 0;
    const borderRadius = variant === 'string' ? 0 : undefined;
    const s = customHeight || (size === 'large' ? 48 : 40);
    const height = variant === 'string' ? 'auto' : s;
    const width = variant === 'string' ? undefined : w;

    const minWidth = (() => {
      if (variant === 'string') {
        return s;
      }
      if (Number.isFinite(mw)) {
        return Math.max(mw, s);
      }
      return s;
    })();

    const baseColor = (() => {
      if (disabled) {
        return theme.palette.colors.grey300;
      }
      if (color === 'secondary') {
        return theme.palette.secondary.main;
      }
      if (color === 'tertiary') {
        return theme.palette.primary.dark;
      }
      if (color === 'error') {
        return theme.palette.error.main;
      }
      return theme.palette.primary.main;
    })();

    const focusBaseColor = theme.palette.colors.grey600;

    const actionColor = (() => {
      if (color === 'secondary') {
        return theme.palette.secondary.dark;
      }
      if (color === 'error') {
        return theme.palette.error.dark;
      }
      return theme.palette.secondary.main;
    })();

    const textColor = (() => {
      if (variant === 'contained') {
        return theme.palette.common.white;
      }
      return baseColor;
    })();

    const focusTextColor = (() => {
      if (variant === 'contained') {
        return theme.palette.common.white;
      }
      return focusBaseColor;
    })();

    const hoverTextColor = (() => {
      if (variant === 'contained') {
        return theme.palette.common.white;
      }
      return actionColor;
    })();

    const backgroundColor = (() => {
      if (variant === 'contained') {
        return baseColor;
      }
      if (['string', 'text'].includes(variant)) {
        return 'transparent';
      }
      return theme.palette.common.white;
    })();

    const focusBackgroundColor = (() => {
      if (variant === 'contained') {
        return focusBaseColor;
      }
      if (['string', 'text'].includes(variant)) {
        return 'transparent';
      }
      return theme.palette.common.white;
    })();

    const hoverBackgroundColor = (() => {
      if (variant === 'contained') {
        return actionColor;
      }
      if (variant === 'string') {
        return 'transparent';
      }
      if (variant === 'text') {
        return alpha(theme.palette.colors.grey900, 0.1);
      }
      return theme.palette.common.white;
    })();

    const typography = (() => {
      if (theme.text[textVariant]) {
        return theme.text[textVariant];
      }
      if (size === 'large') {
        return theme.text.caption1;
      }
      if (size === 'medium') {
        return theme.text.caption2;
      }
      return theme.text.caption3;
    })();

    const rippleDisplay = (() => {
      if (variant === 'string') {
        if (disableRipple === false) {
          return undefined;
        }
        return 'none';
      }
      return undefined;
    })();

    const rippleFocusVisibleDisplay = (() => {
      if (variant === 'string') {
        return 'block';
      }
      return undefined;
    })();

    const result = {
      height,
      color: textColor,
      borderWidth,
      borderStyle: 'solid',
      borderColor: baseColor,
      boxShadow: 'none',
      paddingLeft: offsetX,
      paddingRight: offsetX,
      paddingTop: offsetY,
      paddingBottom: offsetY,
      backgroundColor,
      borderRadius,
      transition: theme.transitions.create(
        ['background-color', 'color', 'width', 'height', 'transform'],
        {
          duration: theme.transitions.duration.shortest,
        },
      ),
      minWidth,
      width,
      maxWidth,

      overflow: 'hidden',
      ...typography,
      lineHeight: 1,
      gap: theme.spacing(gap),

      '& > .MuiButton-label': {
        gap: 'inherit',
      },
      '& > .MuiTouchRipple-root': {
        display: rippleDisplay,
      },
      '&.Mui-disabled': {
        color: textColor,
      },
      // Comment to disable focus colors
      '&:focus, &:active': {
        borderColor: focusBaseColor,
        backgroundColor: focusBackgroundColor,
        color: focusTextColor,
      },
      '&:hover': {
        backgroundColor: hoverBackgroundColor,
        color: hoverTextColor,
        borderColor: actionColor,
      },
      '&:focus-visible': {
        '& > .MuiTouchRipple-root': {
          display: rippleFocusVisibleDisplay,
        },
      },
    };
    return result;
  },
  loading: {
    opacity: 0.5,
    pointerEvents: 'none',
    transform: 'scale(0.9)',
  },
  spinner: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconHandler: {
    '&:hover': {
      '& $icon': {
        backgroundColor: alpha(theme.palette.colors.grey900, 0.1),
      },
    },
  },
  icon: {},
}));

const Button2 = forwardRef((props, ref) => {
  const {
    gap = 1,
    height,
    children,
    color = 'primary',
    variant = 'contained',
    size = 'large',
    fullWidth: fw,
    width,
    maxWidth,
    minWidth,
    loading,
    className,
    disabled,
    iconLeft,
    disableRipple,
    nopadding,
    textVariant,
    ...rest
  } = props;

  const fullWidth = variant === 'string' ? false : fw;

  const loadingSize = useMemo(() => {
    if (variant === 'string') {
      if (size === 'small') {
        return 12;
      }
      if (size === 'medium') {
        return 14;
      }
      return 17;
    }
    return 25;
  }, [variant, size]);

  const classes = useStyles({
    width,
    minWidth,
    maxWidth,
    variant,
    color,
    size,
    gap,
    height,
    disabled,
    disableRipple,
    nopadding,
    textVariant,
  });
  const rootClasses = cx(
    classes.root,
    classes.iconHandler,
    loading && classes.loading,
    className,
  );

  return (
    <MuiButton
      ref={ref}
      fullWidth={fullWidth}
      className={rootClasses}
      disabled={disabled}
      {...rest}
    >
      {['string', 'text'].includes(variant) && iconLeft && (
        <IconButton
          disableRipple
          size="small"
          component="span"
          variant="contained"
          className={classes.icon}
          disabled={disabled}
          tabIndex={-1}
        >
          {iconLeft}
        </IconButton>
      )}

      {children}

      {loading && (
        <div className={classes.spinner}>
          <CircularProgress size={loadingSize} color="inherit" />
        </div>
      )}
    </MuiButton>
  );
});

export default Button2;
